import type {
  ActivityLogPhaseResponse,
  ExecutionDashboardResponse,
  TestPlanResponse,
} from '../../interfaces/api/responses/runResults.response';
import type {
  ActivityLogPhase,
  ExecutionDashboard,
  ITestSuite,
  RunLogFile,
} from '../../interfaces/ui';

export function toRunLogFiles(response: unknown): RunLogFile[] {
  if (
    !response ||
    typeof response !== 'object' ||
    !('data' in response) ||
    !Array.isArray(response.data)
  ) {
    throw new Error('Invalid run logs response.');
  }
  return response.data.map((file: unknown) => {
    if (
      !file ||
      typeof file !== 'object' ||
      !('fileName' in file) ||
      typeof file.fileName !== 'string' ||
      !file.fileName.trim() ||
      !('content' in file) ||
      typeof file.content !== 'string'
    ) {
      throw new Error('Invalid log file in run logs response.');
    }
    return { fileName: file.fileName, content: file.content };
  });
}

export function toActivityLogPhase(
  response: ActivityLogPhaseResponse,
): ActivityLogPhase {
  return {
    phase: response.phase,
    status: response.status,
    events: response.events,
    startTime: response.startTs,
    endTime: response.endTs,
    durationSeconds: response.durationSec,
  };
}

export function toExecutionDashboard(
  response: ExecutionDashboardResponse,
): ExecutionDashboard {
  return {
    status: response.status,
    dashboardUrl: response.data.dashboardUrl,
    reportUrl: response.data.reportUrl || '',
  };
}

export function toTestSuites(response: TestPlanResponse): ITestSuite[] {
  const suites = response.data?.suites ?? response.suites ?? [];

  return suites.map((suite, suiteIndex) => ({
    name: suite.name || `Suite ${suiteIndex + 1}`,
    tests: (suite.tests || []).map((test, testIndex) => ({
      name: test.title || test.name || `Test ${testIndex + 1}`,
      title: test.title || test.name || `Test ${testIndex + 1}`,
      status: test.status,
      reason: test.reason || '',
      preconditions: test.preconditions || [],
      date: test.date,
      runId: test.runId,
      specPath: test.specPath,
      steps: (test.steps || []).map(step => ({
        instruction: step.step || '',
        expectedResults: Array.isArray(step.expect) ? step.expect : [],
      })),
    })),
  }));
}
